import { ArrowLeft, Save } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

const CONTRAPESOS = ["16 N", "32 N", "48 N", "64 N", "80 N", "96 N", "112 N", "128 N"];
const ESPECIMENES_RECHAZADOS = Array.from({length: 11}, (_, i) => i.toString());

interface FormData {
  fecha: string
  hora: string
  grados: string
  hr: string
  capacidadEquipo: string
  urdimbre: string
  trama: string
  especimenesRechazados: string
  estadoEspecimenes: string // Cambiado para permitir múltiples valores: "secos", "humedos", "secos,humedos"
  no1_urdimbre: string
  no1_trama: string
  no2_urdimbre: string
  no2_trama: string
  no3_urdimbre: string
  no3_trama: string
  no4_urdimbre: string
  no4_trama: string
  no5_urdimbre: string
  no5_trama: string
  promedioUrdimbre: string
  promedioTrama: string
  // Campos adicionales para la segunda tabla (HÚMEDOS)
  no1_urdimbre_2?: string
  no1_trama_2?: string
  no2_urdimbre_2?: string
  no2_trama_2?: string
  no3_urdimbre_2?: string
  no3_trama_2?: string
  no4_urdimbre_2?: string
  no4_trama_2?: string
  no5_urdimbre_2?: string
  no5_trama_2?: string
  promedioUrdimbre_2?: string
  promedioTrama_2?: string
  textoPromedioUrdimbre_2?: string
  textoPromedioTrama_2?: string
  realizo: string
  superviso: string
  observaciones: string
  mostrarObservaciones: boolean
  tipoMaterial: string,
  numeroSolicitud?: string,
  idTermohigrometro?: string,
  textoPromedioUrdimbre?: string,
  textoPromedioTrama?: string,
  coeficienteVariacion?: string, // Nuevo campo
  ROW_ID?: string
}

interface NMXA109FormProps {
  pedidoNumero: string
  onBack: () => void
  onSave: (data: FormData) => void
  realizoUser?: string
  initialData?: Partial<FormData>
  mode?: 'create' | 'view'
  rol?: string // <-- NUEVO: rol del usuario
  onRefreshBackend?: () => void
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function NMXA109Form({ pedidoNumero, onBack, onSave, realizoUser, initialData = {}, mode = 'create', rol, onRefreshBackend, pedidoRowId, onAutosave }: NMXA109FormProps) {
  const globalRol = typeof useUserRol === 'function' ? useUserRol() : undefined;
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol;

  const [formData, setFormData] = useState<FormData>({
    fecha: new Date().toISOString().split('T')[0],
    hora: new Date().toTimeString().split(' ')[0].substring(0, 5),
    grados: "",
    hr: "",
    capacidadEquipo: "",
    urdimbre: "",
    trama: "",
    especimenesRechazados: "",
    estadoEspecimenes: "secos",
    no1_urdimbre: "",
    no1_trama: "",
    no2_urdimbre: "",
    no2_trama: "",
    no3_urdimbre: "",
    no3_trama: "",
    no4_urdimbre: "",
    no4_trama: "",
    no5_urdimbre: "",
    no5_trama: "",
    promedioUrdimbre: "",
    promedioTrama: "",
    // Campos para la segunda tabla (HÚMEDOS)
    no1_urdimbre_2: "",
    no1_trama_2: "",
    no2_urdimbre_2: "",
    no2_trama_2: "",
    no3_urdimbre_2: "",
    no3_trama_2: "",
    no4_urdimbre_2: "",
    no4_trama_2: "",
    no5_urdimbre_2: "",
    no5_trama_2: "",
    promedioUrdimbre_2: "",
    promedioTrama_2: "",
    textoPromedioUrdimbre_2: "",
    textoPromedioTrama_2: "",
    ...initialData,
    realizo: mode === 'create' && realizoUser ? realizoUser : (initialData?.realizo || ""),
    superviso: "",
    observaciones: "",
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
    tipoMaterial: initialData.tipoMaterial ?? "TEXTIL",
    numeroSolicitud: initialData.numeroSolicitud || "",
    idTermohigrometro: initialData.idTermohigrometro || "",
    textoPromedioUrdimbre: initialData.textoPromedioUrdimbre || "",
    textoPromedioTrama: initialData.textoPromedioTrama || "",
    coeficienteVariacion: initialData.coeficienteVariacion || "", // Nuevo campo
    ROW_ID: (initialData as any)?.ROW_ID || (initialData as any)?.row_id || ""
  })

  useEffect(() => {
  }, [initialData, formData.ROW_ID]);

  useEffect(() => {
  }, []);

  const [editMode, setEditMode] = useState(false)
  const [estatus, setEstatus] = useState<string>(initialData && (initialData as any).ACEPTADO ? (initialData as any).ACEPTADO : '')

  // Set realizo from realizoUser SOLO en modo create y SOLO si es diferente
  useEffect(() => {
    if (mode === 'create' && realizoUser && formData.realizo !== realizoUser) {
      setFormData(prev => ({ ...prev, realizo: realizoUser }))
    }
  }, [mode, realizoUser])

  // Calcular promedios automáticamente para ambas tablas
  useEffect(() => {
    // Tabla 1 (SECOS) - campos existentes
    const valoresUrdimbre = [
      parseFloat(formData.no1_urdimbre) || 0,
      parseFloat(formData.no2_urdimbre) || 0,
      parseFloat(formData.no3_urdimbre) || 0,
      parseFloat(formData.no4_urdimbre) || 0,
      parseFloat(formData.no5_urdimbre) || 0
    ].filter(val => val > 0)

    const valoresTrama = [
      parseFloat(formData.no1_trama) || 0,
      parseFloat(formData.no2_trama) || 0,
      parseFloat(formData.no3_trama) || 0,
      parseFloat(formData.no4_trama) || 0,
      parseFloat(formData.no5_trama) || 0
    ].filter(val => val > 0)

    const promedioUrdimbre = valoresUrdimbre.length > 0
      ? (valoresUrdimbre.reduce((sum, val) => sum + val, 0) / valoresUrdimbre.length).toFixed(2)
      : ""

    const promedioTrama = valoresTrama.length > 0
      ? (valoresTrama.reduce((sum, val) => sum + val, 0) / valoresTrama.length).toFixed(2)
      : ""

    // Tabla 2 (HÚMEDOS) - campos con sufijo _2
    const valoresUrdimbre_2 = [
      parseFloat(formData.no1_urdimbre_2 || '') || 0,
      parseFloat(formData.no2_urdimbre_2 || '') || 0,
      parseFloat(formData.no3_urdimbre_2 || '') || 0,
      parseFloat(formData.no4_urdimbre_2 || '') || 0,
      parseFloat(formData.no5_urdimbre_2 || '') || 0
    ].filter(val => val > 0)

    const valoresTrama_2 = [
      parseFloat(formData.no1_trama_2 || '') || 0,
      parseFloat(formData.no2_trama_2 || '') || 0,
      parseFloat(formData.no3_trama_2 || '') || 0,
      parseFloat(formData.no4_trama_2 || '') || 0,
      parseFloat(formData.no5_trama_2 || '') || 0
    ].filter(val => val > 0)

    const promedioUrdimbre_2 = valoresUrdimbre_2.length > 0
      ? (valoresUrdimbre_2.reduce((sum, val) => sum + val, 0) / valoresUrdimbre_2.length).toFixed(2)
      : ""

    const promedioTrama_2 = valoresTrama_2.length > 0
      ? (valoresTrama_2.reduce((sum, val) => sum + val, 0) / valoresTrama_2.length).toFixed(2)
      : ""

    setFormData(prev => ({
      ...prev,
      promedioUrdimbre,
      promedioTrama,
      promedioUrdimbre_2,
      promedioTrama_2
    }))
  }, [
    formData.no1_urdimbre, formData.no1_trama,
    formData.no2_urdimbre, formData.no2_trama,
    formData.no3_urdimbre, formData.no3_trama,
    formData.no4_urdimbre, formData.no4_trama,
    formData.no5_urdimbre, formData.no5_trama,
    formData.no1_urdimbre_2, formData.no1_trama_2,
    formData.no2_urdimbre_2, formData.no2_trama_2,
    formData.no3_urdimbre_2, formData.no3_trama_2,
    formData.no4_urdimbre_2, formData.no4_trama_2,
    formData.no5_urdimbre_2, formData.no5_trama_2
  ])

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    // Solo sincroniza todos los datos del backend si NO es modo create
    if (mode !== 'create' && initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        realizo: initialData.realizo || prev.realizo || '', // Mantiene el valor del backend
        superviso: initialData.superviso || prev.superviso || '',
      }))
    }
    setEstatus((initialData as any)?.ACEPTADO || '')
    setEditMode(false)
  }, [initialData, mode]) // Removido realizoUser de las dependencias

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      onSave(formData)
    } finally {
      setGuardando(false)
    }
  }

  const formPayload = () => {
    const { ROW_ID, ...rest } = formData as any
    return rest
  }

  // Solo editable cuando es nuevo (mode === 'create'), solo lectura al revisar
  const isReadOnly = mode === 'view' && !editMode;

  // --- NUEVO: Obtener nombre del supervisor ---
  const getSupervisorName = () => {
    if (realizoUser) return realizoUser;
    if (typeof globalRol === 'string') return globalRol;
    return '';
  }

  // --- NUEVO: Acciones aceptar/rechazar/editar ---
  const [autosaveRowId, setAutosaveRowId] = useState<string>((formData as any)?.ROW_ID || (initialData as any)?.ROW_ID || (initialData as any)?.row_id || '')
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '')
  const rowId = autosaveRowId
  const nomId = autosaveNomId
  const pruebaValue = (formData as any)?.PRUEBA || (initialData as any)?.PRUEBA || '';

  // Lógica robusta para saber si la prueba está aceptada o rechazada
  const aceptadoRaw = (initialData as any)?.ACEPTADO ?? (initialData as any)?.aceptado;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : (estatus || '');
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado' || estatus === 'aceptado' || estatus === 'rechazado';

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-109-INNTEX-2012 RESISTENCIA AL RASGADO',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onBack()
  }

  const handleAccion = async (accion: 'aceptado' | 'rechazado') => {
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-109?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-109?';
    if (!window.confirm(confirmMsg)) return;
    const payload = {
      ...formData,
      realizo: realizoUser || formData.realizo || '',
      superviso: realizoUser || formData.superviso || '',
      SUPERVISO: realizoUser || formData.superviso || '', // <-- igual que AnalisisQuimicoForm
      ROW_ID: rowId,
      PEDIDO: pedidoNumero,
      PRUEBA: pruebaValue,
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion
    };
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, undefined, (payload as any).superviso, nomId);
      setEstatus('aceptado');
      setEditMode(false);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    }
  };

  const handleEditar = () => {
    setEditMode(true)
  }

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-109? Esta acción no se puede deshacer.')) return;
    const payload = {
      ...formData,
      realizo: realizoUser || formData.realizo || '',
      superviso: realizoUser || formData.superviso || '',
      SUPERVISO: realizoUser || formData.superviso || '', // <-- igual que AnalisisQuimicoForm
      ROW_ID: rowId,
      PEDIDO: pedidoNumero,
      PRUEBA: pruebaValue,
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  }

  // Estados para el modal de rechazo
  const [rechazoModalOpen, setRechazoModalOpen] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  // Handler para abrir el modal de rechazo
  const handleRechazar = () => setRechazoModalOpen(true);

  // Handler para enviar el rechazo con razón
  const handleRechazoSubmit = async (razon: string) => {
    setRechazoLoading(true);
    const confirmMsg = 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-109?';
    if (!window.confirm(confirmMsg)) {
      setRechazoLoading(false);
      setRechazoModalOpen(false);
      return;
    }
    const payload = {
      ...formData,
      realizo: realizoUser || formData.realizo || '',
      superviso: realizoUser || formData.superviso || '',
      SUPERVISO: realizoUser || formData.superviso || '', // <-- igual que AnalisisQuimicoForm
      ROW_ID: rowId,
      PEDIDO: pedidoNumero,
      PRUEBA: pruebaValue,
      ACEPTAR: 'rechazado',
      ACEPTADO: 'rechazado',
      RAZON_RECHAZO: razon
    };
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, 'rechazado', razon, undefined, nomId);
      setEstatus('rechazado');
      setEditMode(false);
      setRechazoModalOpen(false);
      toast.success('Prueba rechazada correctamente.');
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    } finally {
      setRechazoLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Modal de rechazo */}
      <RechazoModal
        open={rechazoModalOpen}
        onClose={() => setRechazoModalOpen(false)}
        onSubmit={handleRechazoSubmit}
        loading={rechazoLoading}
      />
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <div>
          <h1 className="text-2xl font-bold leading-tight">NMX-A-109-INNTEX-2012</h1>
          <p className="uppercase text-muted-foreground tracking-wide text-base font-medium mt-1">DETERMINACIÓN DE LA RESISTENCIA AL RASGADO</p>
          <p className="text-sm text-primary font-medium mt-1">Método: MET-7.2-13</p>
          <p className="text-muted-foreground">Pedido: {pedidoNumero}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Condiciones de Ensayo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <Label htmlFor="fecha">FECHA</Label>
              <Input
                id="fecha"
                type="date"
                value={formData.fecha}
                onChange={(e) => handleInputChange('fecha', e.target.value)}
                readOnly={isReadOnly}
                className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
                pattern="\d{4}-\d{2}-\d{2}"
              />
            </div>
            <div>
              <Label htmlFor="hora">HORA</Label>
              <Input
                id="hora"
                type="time"
                value={formData.hora}
                onChange={(e) => { if (e.target.value) handleInputChange('hora', e.target.value) }}
                readOnly={isReadOnly}
                className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
              />
            </div>
            <div>
              <Label htmlFor="grados">°C</Label>
              <Input
                id="grados"
                type="number"
                step="0.1"
                placeholder="20.0"
                value={formData.grados}
                onChange={(e) => handleInputChange('grados', e.target.value)}
                readOnly={isReadOnly}
                className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
              />
            </div>
            <div>
              <Label htmlFor="hr">% HR</Label>
              <Input
                id="hr"
                type="number"
                step="0.1"
                placeholder="65.0"
                value={formData.hr}
                onChange={(e) => handleInputChange('hr', e.target.value)}
                readOnly={isReadOnly}
                className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="urdimbre">URDIMBRE</Label>
              <div className="flex items-center gap-2">
                <Select
                  value={formData.urdimbre}
                  onValueChange={(value) => handleInputChange('urdimbre', value)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}>
                    <SelectValue placeholder="Selecciona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRAPESOS.map((peso) => (
                      <SelectItem key={peso} value={peso}>{peso}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-muted-foreground">N</span>
              </div>
            </div>
            <div>
              <Label htmlFor="trama">TRAMA</Label>
              <div className="flex items-center gap-2">
                <Select
                  value={formData.trama}
                  onValueChange={(value) => handleInputChange('trama', value)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}>
                    <SelectValue placeholder="Selecciona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRAPESOS.map((peso) => (
                      <SelectItem key={peso} value={peso}>{peso}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-muted-foreground">N</span>
              </div>
            </div>
            <div>
              <Label htmlFor="especimenesRechazados">ESPECÍMENES RECHAZADOS</Label>
              <Select
                value={formData.especimenesRechazados}
                onValueChange={(value) => handleInputChange('especimenesRechazados', value)}
                disabled={isReadOnly}
              >
                <SelectTrigger className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}>
                  <SelectValue placeholder="Selecciona..." />
                </SelectTrigger>
                <SelectContent>
                  {ESPECIMENES_RECHAZADOS.map((num) => (
                    <SelectItem key={num} value={num}>{num}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Datos de la Solicitud</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <Label htmlFor="numeroSolicitud">Número de solicitud</Label>
              <Input
                id="numeroSolicitud"
                type="text"
                placeholder="Ejemplo: 12345"
                value={formData.numeroSolicitud || ''}
                onChange={e => handleInputChange('numeroSolicitud', e.target.value)}
                readOnly={isReadOnly}
                className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
              />
            </div>
            <div>
              <Label htmlFor="idTermohigrometro">ID de termohigrómetro</Label>
              <Select
                value={formData.idTermohigrometro || ''}
                onValueChange={(value) => handleInputChange('idTermohigrometro', value)}
                disabled={isReadOnly}
              >
                <SelectTrigger className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}>
                  <SelectValue placeholder="Selecciona..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="E-59">E-59</SelectItem>
                  <SelectItem value="E-57">E-57</SelectItem>
                  <SelectItem value="E-56">E-56</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="coeficienteVariacion">Coeficiente de variación (%)</Label>
              <Input
                id="coeficienteVariacion"
                type="text"
                placeholder="Ejemplo: 2.5 o N/A"
                value={formData.coeficienteVariacion || ''}
                onChange={e => handleInputChange('coeficienteVariacion', e.target.value)}
                readOnly={isReadOnly}
                className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Información del Material</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <Label>TIPO DE MATERIAL EVALUADO</Label>
              <Input
                placeholder="Escribe el tipo de material"
                value={formData.tipoMaterial || ''}
                onChange={e => handleInputChange('tipoMaterial', e.target.value)}
                readOnly={isReadOnly}
                className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
              />
            </div>
            <div>
              <Label>EQUIPO DIGITAL</Label>
              <p className="p-2 bg-muted rounded">Digital</p>
            </div>
            <div>
              <Label>ESTADO DE LOS ESPECÍMENES</Label>
              <div className="flex gap-6 mt-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="secos"
                    checked={formData.estadoEspecimenes?.includes('secos')}
                    onChange={e => {
                      let estados = formData.estadoEspecimenes ? formData.estadoEspecimenes.split(',') : [];
                      if (e.target.checked) {
                        if (!estados.includes('secos')) estados.push('secos');
                      } else {
                        estados = estados.filter(est => est !== 'secos');
                      }
                      handleInputChange('estadoEspecimenes', estados.join(','));
                    }}
                    disabled={isReadOnly}
                  />
                  <Label htmlFor="secos">ACONDICIONADOS</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="humedos"
                    checked={formData.estadoEspecimenes?.includes('humedos')}
                    onChange={e => {
                      let estados = formData.estadoEspecimenes ? formData.estadoEspecimenes.split(',') : [];
                      if (e.target.checked) {
                        if (!estados.includes('humedos')) estados.push('humedos');
                      } else {
                        estados = estados.filter(est => est !== 'humedos');
                      }
                      handleInputChange('estadoEspecimenes', estados.join(','));
                    }}
                    disabled={isReadOnly}
                  />
                  <Label htmlFor="humedos">HÚMEDOS</Label>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla SECOS - Solo se muestra si SECOS está seleccionado */}
      {formData.estadoEspecimenes?.includes('secos') && (
        <Card>
          <CardHeader>
            <CardTitle>Mediciones - ACONDICIONADOS</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">NO.</th>
                    <th className="border border-border p-3 text-center">URDIMBRE</th>
                    <th className="border border-border p-3 text-center">TRAMA</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5].map((num) => (
                    <tr key={num}>
                      <td className="border border-border p-3 text-center">{num}</td>
                      <td className="border border-border p-2">
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="0.0"
                          value={formData[`no${num}_urdimbre` as keyof FormData] as string}
                          onChange={(e) => handleInputChange(`no${num}_urdimbre` as keyof FormData, e.target.value)}
                          className="text-center"
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                        />
                      </td>
                      <td className="border border-border p-2">
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="0.0"
                          value={formData[`no${num}_trama` as keyof FormData] as string}
                          onChange={(e) => handleInputChange(`no${num}_trama` as keyof FormData, e.target.value)}
                          className="text-center"
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-accent">
                    <td className="border border-border p-3 text-center">PROMEDIO</td>
                    <td className="border border-border p-3 text-center">
                      <span className="font-medium">{formData.promedioUrdimbre} {formData.promedioUrdimbre && 'N'}</span>
                    </td>
                    <td className="border border-border p-3 text-center">
                      <span className="font-medium">{formData.promedioTrama} {formData.promedioTrama && 'N'}</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3 text-center">( )</td>
                    <td className="border border-border p-3">
                      <Textarea
                        placeholder="Campo abierto para texto (Urdimbre)"
                        value={formData.textoPromedioUrdimbre || ''}
                        onChange={e => handleInputChange('textoPromedioUrdimbre', e.target.value)}
                        rows={2}
                        readOnly={isReadOnly}
                        className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
                      />
                    </td>
                    <td className="border border-border p-3">
                      <Textarea
                        placeholder="Campo abierto para texto (Trama)"
                        value={formData.textoPromedioTrama || ''}
                        onChange={e => handleInputChange('textoPromedioTrama', e.target.value)}
                        rows={2}
                        readOnly={isReadOnly}
                        className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabla HÚMEDOS - Solo se muestra si HÚMEDOS está seleccionado */}
      {formData.estadoEspecimenes?.includes('humedos') && (
        <Card>
          <CardHeader>
            <CardTitle>Mediciones - HÚMEDOS</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">NO.</th>
                    <th className="border border-border p-3 text-center">URDIMBRE</th>
                    <th className="border border-border p-3 text-center">TRAMA</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5].map((num) => (
                    <tr key={num}>
                      <td className="border border-border p-3 text-center">{num}</td>
                      <td className="border border-border p-2">
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="0.0"
                          value={(formData[`no${num}_urdimbre_2` as keyof FormData] as string) || ''}
                          onChange={(e) => handleInputChange(`no${num}_urdimbre_2` as keyof FormData, e.target.value)}
                          className="text-center"
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                        />
                      </td>
                      <td className="border border-border p-2">
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="0.0"
                          value={(formData[`no${num}_trama_2` as keyof FormData] as string) || ''}
                          onChange={(e) => handleInputChange(`no${num}_trama_2` as keyof FormData, e.target.value)}
                          className="text-center"
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-accent">
                    <td className="border border-border p-3 text-center">PROMEDIO</td>
                    <td className="border border-border p-3 text-center">
                      <span className="font-medium">{formData.promedioUrdimbre_2} {formData.promedioUrdimbre_2 && 'N'}</span>
                    </td>
                    <td className="border border-border p-3 text-center">
                      <span className="font-medium">{formData.promedioTrama_2} {formData.promedioTrama_2 && 'N'}</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3 text-center">( )</td>
                    <td className="border border-border p-3">
                      <Textarea
                        placeholder="Campo abierto para texto (Urdimbre - HÚMEDOS)"
                        value={formData.textoPromedioUrdimbre_2 || ''}
                        onChange={e => handleInputChange('textoPromedioUrdimbre_2', e.target.value)}
                        rows={2}
                        readOnly={isReadOnly}
                        className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
                      />
                    </td>
                    <td className="border border-border p-3">
                      <Textarea
                        placeholder="Campo abierto para texto (Trama - HÚMEDOS)"
                        value={formData.textoPromedioTrama_2 || ''}
                        onChange={e => handleInputChange('textoPromedioTrama_2', e.target.value)}
                        rows={2}
                        readOnly={isReadOnly}
                        className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Personal y Observaciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label htmlFor="realizo">REALIZÓ</Label>
              <Input
                id="realizo"
                placeholder="Nombre del técnico"
                value={formData.realizo}
                readOnly
                className="bg-muted-foreground/10 cursor-not-allowed"
              />
            </div>
            <div>
              <Label htmlFor="superviso">SUPERVISÓ</Label>
              <Input
                id="superviso"
                placeholder="Nombre del supervisor"
                value={formData.superviso}
                readOnly
                className="bg-muted-foreground/10 cursor-not-allowed"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Label htmlFor="observaciones">OBSERVACIONES</Label>
              <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                Mostrar en Reporte Cliente
                <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                  onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
              </span>
            </div>
            <Textarea
              id="observaciones"
              placeholder="Escriba sus observaciones aquí..."
              value={formData.observaciones}
              onChange={(e) => handleInputChange('observaciones', e.target.value)}
              rows={4}
              readOnly={isReadOnly}
              className={isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : undefined}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={handleBackFlush}>
          Cancelar
        </Button>
        {/* Botones de acciones en modo view y no aceptado/rechazado SOLO PARA ADMIN */}
        {mode === 'view' && !editMode && estatus !== 'aceptado' && estatus !== 'rechazado' && puedeRevisar && (
          <>
            <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
            <Button variant="destructive" onClick={handleRechazar}>Rechazar</Button>
            <Button variant="secondary" onClick={handleEditar}>Editar</Button>
          </>
        )}
        {/* Botón guardar edición SOLO PARA ADMIN */}
        {mode === 'view' && editMode && estatus !== 'aceptado' && estatus !== 'rechazado' && puedeRevisar && (
          <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
        )}
        {/* Botón guardar en modo create */}
        {mode === 'create' && (
          <Button onClick={handleSave} disabled={guardando}>
            <Save className="h-4 w-4 mr-2" />
            {guardando ? 'Guardando...' : 'Guardar Formulario'}
          </Button>
        )}
      </div>
    </div>
  )
}